import React, { useState, useEffect} from "react";
import {
  Menu,
  X,
  Mail,
  MessageCircle,
  Facebook,
  ChevronRight,
  BookOpen,
  Users,
  Sparkles,
  Baby,
  Code2,
  BookMarked,
  Globe2,
} from "lucide-react";
import logo from "../img/publiclogo.png";
/* ============================================================
   DESIGN TOKENS
   Navy   #16233F  (seal band / primary ink)
   Maroon #7A1B2A  (seal band / accent)
   Gold   #B08D57  (trim, sparingly)
   Cream  #F4EFE4  (page background)
   Paper  #FBF8F3  (card background)
   Ink    #201C18  (body text)
   ============================================================ */

const T = {
  navy: "#16233F",
  navyLight: "#233457",
  maroon: "#7A1B2A",
  maroonLight: "#9C2740",
  gold: "#B08D57",
  cream: "#F4EFE4",
  paper: "#FBF8F3",
  ink: "#201C18",
  inkSoft: "#5B5347",
};


/* ============================================================
   DATA — Clubs & programs
   ============================================================ */
const CLUBS = [
  {
    id: "italian",
    name: "Italian Club",
    tag: "Language",
    icon: Globe2,
    blurb:
      "The club that started it all — full pathway from first words to professional fluency, plus media and outreach projects for the wider Italian-speaking community.",
    items: [
      "Italian Learning Course",
      "Italian Conversation Course",
      "Italian Translation Course",
      "Italian for Call Centre",
      "Italian for Tourism",
      "Radio SpazioItalia (RSI)",
      "Italian Magazine",
      "Other Italian Projects",
    ],
  },
  {
    id: "english",
    name: "English Club",
    tag: "Language",
    icon: Globe2,
    blurb:
      "Structured levels for every stage of English, rounded out with a cinema club and a student-run magazine to put the language to real use.",
    items: [
      "Elementary Course",
      "Intermediate Course",
      "Advanced Course",
      "Conversation Course",
      "English Cinema Club",
      "English Magazine",
      "Other Workshops",
    ],
  },
  {
    id: "french",
    name: "French Club",
    tag: "Language",
    icon: Globe2,
    blurb: "A clear three-level track for building French from the ground up.",
    items: ["Elementary Course", "Intermediate Course", "Advanced Course"],
  },
  {
    id: "german",
    name: "German Club",
    tag: "Language",
    icon: Globe2,
    blurb: "Grammar, vocabulary and conversation practice across three progressive levels.",
    items: ["Elementary Course", "Intermediate Course", "Advanced Course"],
  },
  {
    id: "turkish",
    name: "Turkish Club",
    tag: "Language",
    icon: Globe2,
    blurb: "Learn Turkish step by step, from everyday basics to confident conversation.",
    items: ["Elementary Course", "Intermediate Course", "Advanced Course"],
  },
  {
    id: "programming",
    name: "Programming Club",
    tag: "coding",
    icon: Code2,
    blurb:
      "Our newest club is being built right now. Follow our Facebook page to be the first to know when enrollment opens.",
    items: ["programing for Kids "],
    // comingSoon: true,
  },
  {
    id: "quran",
    name: "Quran Club",
    tag: "Coming Soon",
    icon: BookMarked,
    blurb:
      "A dedicated space for memorization and Tajweed is on the way. Follow our Facebook page to be the first to know when enrollment opens.",
    items: ["قرآن " , "عقيدة " ,  "سيرة  " , "تدبر "],
    // comingSoon: true,
  },
  {
    id: "kids",
    name: "MTC Kids Club",
    tag: "For Kids",
    icon: Baby,
    blurb:
      "A whole world of learning made for children — languages, reading, numbers and code, wrapped in workshops and their very own magazine.",
    items: [
      'Online Magazine — "Sonbola"',
      "Workshops",
      "Reading & Writing Arabic Club",
      "English Club for Kids",
      "French Club for Kids",
      "Mental Math for Kids",
      "Programming Club for Kids",
      "Other Projects for Kids",
    ],
  },
  {
    id: "arabic-foreigners",
    name: "Arabic Club for Foreigners",
    tag: "For Italian Speakers",
    icon: Users,
    blurb:
      "Arabic taught specifically for our Italian-speaking members, from first letters to a course built around your own goals.",
    items: [
      "Basic Course",
      "Intermediate Course",
      "Advanced Course",
      "Custom Your Favourite Course",
    ],
  },
];

const WHATSAPP_NUMBER = "201042231306";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hello MTC, I'd like to know more about your clubs."
)}`;
const EMAIL = "mtcommunity2026@gmail.com";
const FACEBOOK_LINK = "https://www.facebook.com/profile.php?id=61592654781495";

const NAV_LINKS = [
  { href: "#about", label: "About" },
  { href: "#clubs", label: "Clubs" },
  { href: "#kids", label: "Kids" },
  { href: "#contact", label: "Contact" },
];

/* ============================================================
   SMALL UI PIECES
   ============================================================ */
function LaurelDivider() {
  return (
    <div className="flex items-center justify-center gap-3 select-none" aria-hidden="true">
      <span style={{ width: 46, height: 1, background: T.gold, opacity: 0.6 }} />
      <svg width="18" height="18" viewBox="0 0 24 24" style={{ color: T.maroon }}>
        <circle cx="12" cy="12" r="4" fill="currentColor" />
        <circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      </svg>
      <span style={{ width: 46, height: 1, background: T.gold, opacity: 0.6 }} />
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div
      className="uppercase font-semibold"
      style={{
        color: T.maroon,
        letterSpacing: "0.22em",
        fontSize: "0.72rem",
        fontFamily: "'IBM Plex Mono', monospace",
      }}
    >
      {children}
    </div>
  );
}

/* ============================================================
   MAIN PAGE
   ============================================================ */
export default function MTCLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeClub, setActiveClub] = useState(CLUBS[0].id);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const club = CLUBS.find((c) => c.id === activeClub) || CLUBS[0];

  return (
    <div
      style={{
        background: T.cream,
        color: T.ink,
        fontFamily: "'Inter', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
        .disp { font-family: 'Playfair Display', serif; }
        .mono { font-family: 'IBM Plex Mono', monospace; }
        .ring-frame {
          position: relative;
        }
        .ring-frame::before {
          content: "";
          position: absolute;
          inset: -6px;
          border: 1px solid ${T.gold};
          opacity: 0.45;
          pointer-events: none;
        }
        .stamp {
          transition: transform .35s ease, opacity .35s ease;
          opacity: 0;
          transform: rotate(-14deg) scale(0.7);
        }
        .club-item:hover .stamp {
          opacity: 1;
          transform: rotate(-14deg) scale(1);
        }
        .navlink { position: relative; }
        .navlink::after {
          content: "";
          position: absolute;
          left: 0; right: 0; bottom: -6px;
          height: 2px;
          background: ${T.maroon};
          transform: scaleX(0);
          transform-origin: left;
          transition: transform .25s ease;
        }
        .navlink:hover::after { transform: scaleX(1); }
        .club-btn[data-active="true"] {
          background: ${T.navy};
          color: ${T.paper};
        }
        .club-btn[data-active="true"] .club-btn-tag { color: ${T.gold}; }
        @media (prefers-reduced-motion: reduce) {
          .stamp, .navlink::after { transition: none; }
        }
        a:focus-visible, button:focus-visible {
          outline: 2px solid ${T.maroon};
          outline-offset: 3px;
        }
      `}</style>

      {/* ============ NAV ============ */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: scrolled ? "rgba(244,239,228,0.94)" : "transparent",
          backdropFilter: scrolled ? "blur(8px)" : "none",
          borderBottom: scrolled ? `1px solid ${T.gold}55` : "1px solid transparent",
          transition: "all .3s ease",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex items-center justify-between" style={{ height: 74 }}>
          <a href="#top" className="flex items-center gap-3">
            <img src={logo} alt="Milano Training Community" style={{ width: 50}} />
            <div className="leading-tight hidden sm:block">
              <div className="disp font-bold" style={{ color: T.navy, fontSize: "1.05rem" }}>
                Milano Training Community
              </div>
              <div className="mono" style={{ color: T.maroon, fontSize: "0.62rem", letterSpacing: "0.14em" }}>
                YOUR PASSION IS YOUR POWER
              </div>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="navlink font-medium"
                style={{ color: T.navy, fontSize: "0.94rem" }}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-semibold"
              style={{
                background: T.maroon,
                color: T.paper,
                padding: "0.6rem 1.15rem",
                borderRadius: 3,
                fontSize: "0.88rem",
              }}
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </div>

          <button
            className="md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            style={{ color: T.navy }}
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden px-5 pb-5" style={{ background: T.paper, borderTop: `1px solid ${T.gold}55` }}>
            <nav className="flex flex-col gap-4 pt-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="font-medium"
                  style={{ color: T.navy }}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 font-semibold"
                style={{ background: T.maroon, color: T.paper, padding: "0.7rem", borderRadius: 3 }}
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* ============ HERO ============ */}
      <section id="top" className="relative overflow-hidden" style={{ borderBottom: `1px solid ${T.gold}44` }}>
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 50% -10%, ${T.navy}10, transparent 60%)`,
          }}
          aria-hidden="true"
        />
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 relative flex flex-col items-center text-center">
          <div className="ring-frame mb-8">
            <img src={logo} alt="Milano Training Community" style={{ width: 152 }} />
          </div>
          <Eyebrow>Milano Training Community</Eyebrow>
          <h1
            className="disp font-bold mt-4"
            style={{ color: T.navy, fontSize: "clamp(2rem, 5vw, 3.4rem)", lineHeight: 1.12, maxWidth: 780 }}
          >
            Your passion is your power.
          </h1>
          <p className="mt-5 max-w-xl" style={{ color: T.inkSoft, fontSize: "1.05rem", lineHeight: 1.7 }}>
            A community of clubs built around languages, culture, faith and skill —
            for every age, taught by people who care about where you're headed.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-9">
            <a
              href="#clubs"
              className="font-semibold flex items-center gap-2"
              style={{ background: T.navy, color: T.paper, padding: "0.85rem 1.7rem", borderRadius: 3 }}
            >
              Explore our Clubs <ChevronRight size={17} />
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold flex items-center gap-2"
              style={{
                border: `1.5px solid ${T.maroon}`,
                color: T.maroon,
                padding: "0.82rem 1.7rem",
                borderRadius: 3,
              }}
            >
              <MessageCircle size={17} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section id="about" className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="grid md:grid-cols-[1fr_1.15fr] gap-12 items-start">
          <div>
            <Eyebrow>About the Community</Eyebrow>
            <h2 className="disp font-bold mt-3" style={{ color: T.navy, fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
              One community, many clubs, one goal.
            </h2>
          </div>
          <div style={{ color: T.inkSoft, fontSize: "1.02rem", lineHeight: 1.85 }}>
            <p>
              Milano Training Community brings language, culture and skill-building together under one
              roof. Every club runs its own courses, projects and media — from radio to magazines — so
              members don't just learn a subject, they join a circle of people who practice it together.
            </p>
            <p className="mt-4">
              Whether you're taking your first Italian lesson, polishing your English for a call
              centre, memorizing Quran, or signing your child up for their first coding workshop —
              there's a club here built for that exact step you're on.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mt-14">
          {[
            { icon: Globe2, title: "5 Language Clubs", desc: "Italian, English, French, German & Turkish, each with structured levels." },
            { icon: Sparkles, title: "Real Projects", desc: "Radio, magazines, cinema nights and workshops built by members." },
            { icon: Baby, title: "Programs for Kids", desc: "A full club made just for younger learners, in Sonbola magazine and beyond." },
          ].map((f, i) => (
            <div
              key={i}
              className="ring-frame"
              style={{ background: T.paper, padding: "1.6rem", border: `1px solid ${T.gold}40` }}
            >
              <f.icon size={22} style={{ color: T.maroon }} />
              <div className="disp font-bold mt-3" style={{ color: T.navy, fontSize: "1.02rem" }}>
                {f.title}
              </div>
              <div className="mt-1.5" style={{ color: T.inkSoft, fontSize: "0.92rem", lineHeight: 1.6 }}>
                {f.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <LaurelDivider />
      </div>

      {/* ============ CLUBS ============ */}
      <section id="clubs" className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Our Clubs</Eyebrow>
          <h2 className="disp font-bold mt-3" style={{ color: T.navy, fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
            Choose a club to see what's inside.
          </h2>
          <p className="mt-3" style={{ color: T.inkSoft }}>
            Tap any club below — its courses and projects will open on the right.
          </p>
        </div>

        <div className="grid md:grid-cols-[300px_1fr] gap-6 md:gap-8">
          {/* club selector */}
          <div
            className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0"
            role="tablist"
            aria-label="Clubs"
          >
            {CLUBS.map((c) => {
              const Icon = c.icon;
              const isActive = c.id === activeClub;
              return (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={isActive}
                  data-active={isActive}
                  onClick={() => setActiveClub(c.id)}
                  className="club-btn flex items-center gap-3 text-left shrink-0 md:shrink"
                  style={{
                    background: isActive ? T.navy : T.paper,
                    color: isActive ? T.paper : T.ink,
                    border: `1px solid ${isActive ? T.navy : T.gold + "40"}`,
                    padding: "0.75rem 1rem",
                    borderRadius: 3,
                    minWidth: 200,
                  }}
                >
                  <Icon size={18} style={{ color: isActive ? T.gold : T.maroon, flexShrink: 0 }} />
                  <span>
                    <span className="block font-semibold" style={{ fontSize: "0.9rem" }}>
                      {c.name}
                    </span>
                    <span
                      className="club-btn-tag block mono"
                      style={{ fontSize: "0.62rem", letterSpacing: "0.08em", color: isActive ? T.gold : T.inkSoft }}
                    >
                      {c.tag.toUpperCase()}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* club panel */}
          <div className="ring-frame" style={{ background: T.paper, border: `1px solid ${T.gold}40`, padding: "2rem" }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="mono" style={{ color: T.maroon, fontSize: "0.7rem", letterSpacing: "0.16em" }}>
                  {club.tag.toUpperCase()}
                </div>
                <h3 className="disp font-bold mt-1" style={{ color: T.navy, fontSize: "1.5rem" }}>
                  {club.name}
                </h3>
              </div>
             <img src={logo} alt="Milano Training Community" style={{ width: 60 }} />
            </div>

            <p className="mt-4" style={{ color: T.inkSoft, lineHeight: 1.75 }}>
              {club.blurb}
            </p>

            {club.comingSoon ? (
              <a
                href={FACEBOOK_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-semibold mt-6"
                style={{ color: T.maroon, borderBottom: `1.5px solid ${T.maroon}`, paddingBottom: 2 }}
              >
                <Facebook size={16} /> Follow updates on Facebook
              </a>
            ) : (
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {club.items.map((item, i) => (
                  <li
                    key={i}
                    className="club-item flex items-center gap-3 py-2"
                    style={{ borderBottom: `1px dashed ${T.gold}55` }}
                  >
                    <span className="stamp" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="none" stroke={T.maroon} strokeWidth="1.5" />
                        <circle cx="12" cy="12" r="6" fill={T.maroon} opacity="0.15" />
                      </svg>
                    </span>
                    <BookOpen size={14} style={{ color: T.maroon, flexShrink: 0 }} />
                    <span style={{ fontSize: "0.94rem", color: T.ink }}>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <LaurelDivider />
      </div>

      {/* ============ KIDS CALLOUT ============ */}
      <section id="kids" className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <div
          className="ring-frame grid md:grid-cols-[auto_1fr] items-center gap-8"
          style={{ background: T.navy, color: T.paper, padding: "2.5rem", borderRadius: 4 }}
        >
          <div
            className="w-16 h-16 flex items-center justify-center rounded-full shrink-0"
            style={{ background: T.maroon }}
          >
            <Baby size={30} color={T.paper} />
          </div>
          <div>
            <Eyebrow>MTC Kids Club</Eyebrow>
            <h3 className="disp font-bold mt-2" style={{ fontSize: "1.5rem" }}>
              A whole club made just for the youngest members.
            </h3>
            <p className="mt-3" style={{ color: "#D9DEE9", lineHeight: 1.75, maxWidth: 640 }}>
              From reading and writing Arabic to English, French, mental math and their first lines of
              code — plus their own online magazine, "Sonbola." See the full list of kids' programs in
              the Clubs section above.
            </p>
            <button
              onClick={() => {
                setActiveClub("kids");
                document.getElementById("clubs")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 font-semibold mt-5"
              style={{ color: T.gold }}
            >
              View Kids Club programs <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section id="contact" className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Get in Touch</Eyebrow>
          <h2 className="disp font-bold mt-3" style={{ color: T.navy, fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
            Questions about a club? Reach out.
          </h2>
          <p className="mt-3" style={{ color: T.inkSoft }}>
            The fastest way to reach us is WhatsApp — we're happy to help you pick the right course.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="ring-frame flex flex-col items-center text-center gap-3"
            style={{ background: T.maroon, color: T.paper, padding: "2rem 1.5rem" }}
          >
            <MessageCircle size={26} />
            <div className="disp font-bold" style={{ fontSize: "1.05rem" }}>
              WhatsApp
            </div>
            <div className="mono" style={{ fontSize: "0.85rem", letterSpacing: "0.03em" }}>
              +20 10 4223 1306
            </div>
            <span
              className="mt-1 font-semibold"
              style={{ fontSize: "0.82rem", borderBottom: "1.5px solid rgba(255,255,255,0.7)" }}
            >
              Send a message
            </span>
          </a>

          <a
            href={`mailto:${EMAIL}`}
            className="ring-frame flex flex-col items-center text-center gap-3"
            style={{ background: T.paper, color: T.navy, padding: "2rem 1.5rem", border: `1px solid ${T.gold}40` }}
          >
            <Mail size={26} style={{ color: T.maroon }} />
            <div className="disp font-bold" style={{ fontSize: "1.05rem" }}>
              Email
            </div>
            <div className="mono" style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
              {EMAIL}
            </div>
            <span
              className="mt-1 font-semibold"
              style={{ fontSize: "0.82rem", color: T.maroon, borderBottom: `1.5px solid ${T.maroon}` }}
            >
              Write to us
            </span>
          </a>

          <a
            href={FACEBOOK_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="ring-frame flex flex-col items-center text-center gap-3"
            style={{ background: T.navy, color: T.paper, padding: "2rem 1.5rem" }}
          >
            <Facebook size={26} />
            <div className="disp font-bold" style={{ fontSize: "1.05rem" }}>
              Facebook
            </div>
            <div className="mono" style={{ fontSize: "0.8rem" }}>
              Milano Training Community
            </div>
            <span
              className="mt-1 font-semibold"
              style={{ fontSize: "0.82rem", borderBottom: "1.5px solid rgba(255,255,255,0.7)" }}
            >
              Visit our page
            </span>
          </a>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer style={{ background: T.navy, color: "#C9CFDC" }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 grid sm:grid-cols-[auto_1fr_auto] gap-8 items-center">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Milano Training Community" style={{ width: 50}} />
            <div className="disp font-bold" style={{ color: T.paper, fontSize: "1rem" }}>
              Milano Training
              <br />
              Community
            </div>
          </div>

          <nav className="flex flex-wrap gap-x-6 gap-y-2 justify-start sm:justify-center">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} style={{ fontSize: "0.88rem" }} className="hover:opacity-80 text-white">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex gap-3 justify-start sm:justify-end">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="w-9 h-9 flex items-center justify-center rounded-full text-white"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <MessageCircle size={16} />
            </a>
            <a
              href={`mailto:${EMAIL}`}
              aria-label="Email"
              className="w-9 h-9 flex items-center justify-center rounded-full text-white"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <Mail size={16} />
            </a>
            <a
              href={FACEBOOK_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="w-9 h-9 flex items-center justify-center rounded-full text-white"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <Facebook size={16} />
            </a>
          </div>
        </div>
        <div
          className="text-center py-4 mono "
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: "0.72rem", color: "#8891A6" }}
        >
          © {new Date().getFullYear()} Milano Training Community — Your Passion Is Your Power
        </div>
      </footer>
    </div>
  );
}
